import { Alert } from "../../../components/Alert/Alert";
import { FieldRow } from "../../projects/config/FieldRow";
import { SectionCard } from "../../projects/config/SectionCard";
import type { BotsMaturationConfig } from "../config/types";
import { LabelledNumber } from "./LabelledNumber";

interface Props {
  value: BotsMaturationConfig;
  onChange: (patch: Partial<BotsMaturationConfig>) => void;
  dirty: boolean;
}

/**
 * `bots.maturation` — how the simulation takes itself apart as the project
 * grows real players.
 *
 * Mockup: bots-page-shell.html §4, "Maturation". Moved verbatim, minus the
 * closing "Open the Bots page →" link: this IS the Bots page now, and a link
 * that lands the reader where they already are is worse than no link.
 */
export function MaturationSection({ value, onChange, dirty }: Props) {
  return (
    <SectionCard
      id="maturation"
      title="Maturation"
      apiName="bots.maturation"
      dirty={dirty}
    >
      <FieldRow
        name="Retire bots as the game grows"
        apiName="bots.maturation"
        hint="Bots are scaffolding, not foundation. Both rules run on the population worker."
      >
        <LabelledNumber
          label="leave a guild once real members ≥"
          wide
          value={value.leave_when_real_members}
          onChange={(n) => onChange({ leave_when_real_members: n })}
        />
        <LabelledNumber
          label="sleep entirely once the project has ≥"
          wide
          unit="players active in 7 days"
          value={value.sleep_when_active_players}
          onChange={(n) => onChange({ sleep_when_active_players: n })}
        />
        <Alert variant="info">
          Retiring never drops a guild below <code>fill_below_members</code>:
          bots leave one at a time and stop as soon as the guild would dip. The
          project-wide rule counts living players who did something in the last
          seven days — the platform does not measure DAU, and a number that
          merely looked like one would be worse than the honest one.
        </Alert>
      </FieldRow>
    </SectionCard>
  );
}
